/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * PitchDeckAdminPage — visual-settings editor for the investor pitch deck.
 * Lets admins adjust font sizes, text colors, spacing, and other visual
 * properties per section, with live preview link. Settings persist in localStorage.
 */
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, RotateCcw, Eye, Type, Palette, Maximize,
  ChevronDown, ChevronRight, Save, Sliders, Calculator,
} from "lucide-react";
import {
  PitchSettingsProvider,
  usePitchSettings,
} from "../contexts/PitchSettingsContext";
import versions, { DEFAULT_VERSION } from "../data/pitchDeckVersions";

// ── Colour presets for quick selection ──
const grayPresets = [
  { label: "100", value: "text-gray-100", hex: "#F3F4F6" },
  { label: "200", value: "text-gray-200", hex: "#E5E7EB" },
  { label: "300", value: "text-gray-300", hex: "#D1D5DB" },
  { label: "400", value: "text-gray-400", hex: "#9CA3AF" },
  { label: "500", value: "text-gray-500", hex: "#6B7280" },
];

const accentPresets = [
  { label: "Blue",   hex: "#3B82F6" },
  { label: "Teal",   hex: "#4FA9A1" },
  { label: "Purple", hex: "#A78BFA" },
  { label: "Amber",  hex: "#F59E0B" },
  { label: "Rose",   hex: "#F43F5E" },
];

// ── Section metadata for display ──
const SECTION_META = [
  { id: "hero", label: "Overview / Hero" },
  { id: "problem", label: "The Problem" },
  { id: "solution", label: "The Solution" },
  { id: "businessModel", label: "Business Model" },
  { id: "market", label: "Market Opportunity" },
  { id: "traction", label: "Traction & Validation" },
  { id: "unitEconomics", label: "Unit Economics" },
  { id: "competitive", label: "Competitive Landscape" },
  { id: "team", label: "Team" },
  { id: "roadmap", label: "Roadmap" },
  { id: "risks", label: "Dependencies & Risks" },
  { id: "ask", label: "The Ask" },
];

// ── Reusable controls ──

function SliderControl({ label, value, min, max, step = 1, onChange, unit = "px", icon: Icon }) {
  return (
    <div className="flex items-center gap-4">
      {Icon && <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      <div className="flex-1">
        <div className="flex justify-between mb-1">
          <span className="text-sm text-gray-300">{label}</span>
          <span className="text-sm font-mono text-[#60A5FA]">{value}{unit}</span>
        </div>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-[#3B82F6]
                     [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                     [&::-webkit-slider-thumb]:bg-[#3B82F6] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:shadow-lg"
        />
      </div>
    </div>
  );
}

function ColorSwatchRow({ label, presets, value, onChange }) {
  return (
    <div>
      <span className="text-sm text-gray-300 block mb-2">{label}</span>
      <div className="flex gap-2 flex-wrap">
        {presets.map((p) => {
          const isActive = value === (p.value || p.hex);
          return (
            <button
              key={p.label}
              onClick={() => onChange(p.value || p.hex)}
              className={`w-9 h-9 rounded-lg border-2 transition-all flex items-center justify-center text-[10px] font-bold ${
                isActive
                  ? "border-white scale-110 shadow-lg"
                  : "border-transparent hover:border-white/30"
              }`}
              style={{ backgroundColor: p.hex }}
              title={p.label}
            >
              {isActive && <span className="text-white drop-shadow-lg">✓</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Section settings panel ──

function SectionPanel({ sectionId, sectionLabel }) {
  const { settings, updateSection, resetSection, defaultSettings } = usePitchSettings();
  const [open, setOpen] = useState(false);
  const overrides = settings.sections?.[sectionId] || {};
  const global = settings.global;
  const hasOverrides = Object.keys(overrides).length > 0;

  const eff = (key) => overrides[key] ?? global[key];

  return (
    <div className="border border-white/8 rounded-xl overflow-hidden bg-[#0F1826]/60">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-3 w-full px-5 py-3.5 text-left hover:bg-white/[0.02] transition-colors"
      >
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        <span className="text-sm font-medium text-white flex-1">{sectionLabel}</span>
        {hasOverrides && (
          <span className="text-[10px] bg-[#3B82F6]/20 text-[#60A5FA] px-2 py-0.5 rounded-full font-medium">
            customised
          </span>
        )}
      </button>

      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="px-5 pb-5 space-y-5 border-t border-white/5"
        >
          <div className="pt-4" />

          <SliderControl
            label="Font size"
            icon={Type}
            value={eff("fontSize") || eff("baseFontSize")}
            min={12}
            max={24}
            onChange={(v) => updateSection(sectionId, { fontSize: v })}
          />

          <SliderControl
            label="Heading scale"
            icon={Maximize}
            value={eff("headingScale") ?? 1}
            min={0.6}
            max={1.6}
            step={0.05}
            unit="×"
            onChange={(v) => updateSection(sectionId, { headingScale: v })}
          />

          <ColorSwatchRow
            label="Body text colour"
            presets={grayPresets}
            value={eff("bodyTextColor")}
            onChange={(v) => updateSection(sectionId, { bodyTextColor: v })}
          />

          <ColorSwatchRow
            label="Muted / helper text"
            presets={grayPresets}
            value={eff("mutedTextColor")}
            onChange={(v) => updateSection(sectionId, { mutedTextColor: v })}
          />

          <ColorSwatchRow
            label="Accent colour"
            presets={accentPresets}
            value={eff("accentColor")}
            onChange={(v) => updateSection(sectionId, { accentColor: v })}
          />

          {hasOverrides && (
            <button
              onClick={() => resetSection(sectionId)}
              className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1.5 transition-colors pt-1"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset to global defaults
            </button>
          )}
        </motion.div>
      )}
    </div>
  );
}

// ── Main admin page content ──

function AdminContent() {
  const navigate = useNavigate();
  const { settings, updateGlobal, resetAll } = usePitchSettings();
  const g = settings.global;

  return (
    <div className="min-h-screen bg-[#0A0F1A] text-white">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#0A0F1A]/90 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/invest")}
              className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-base font-bold flex items-center gap-2">
                <Sliders className="w-4 h-4 text-[#3B82F6]" />
                Pitch Deck — Visual Settings
              </h1>
              <p className="text-xs text-gray-400">Changes save automatically to your browser</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/invest/admin/economics")}
              className="flex items-center gap-1.5 bg-[#4FA9A1]/15 hover:bg-[#4FA9A1]/25 text-[#4FA9A1] text-sm px-3 py-1.5 rounded-lg transition-colors"
            >
              <Calculator className="w-3.5 h-3.5" />
              Unit Economics
            </button>
            <button
              onClick={() => navigate("/invest")}
              className="flex items-center gap-1.5 bg-[#3B82F6]/15 hover:bg-[#3B82F6]/25 text-[#60A5FA] text-sm px-3 py-1.5 rounded-lg transition-colors"
            >
              <Eye className="w-3.5 h-3.5" />
              Preview
            </button>
            <button
              onClick={resetAll}
              className="flex items-center gap-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm px-3 py-1.5 rounded-lg transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset all
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-10">
        {/* ── Global settings ── */}
        <section>
          <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
            <Palette className="w-5 h-5 text-[#4FA9A1]" />
            Global Defaults
          </h2>
          <p className="text-sm text-gray-400 mb-6">
            These apply to every section unless overridden below.
          </p>

          <div className="bg-[#0F1826]/60 border border-white/8 rounded-2xl p-6 space-y-6">
            <SliderControl
              label="Base font size"
              icon={Type}
              value={g.baseFontSize}
              min={12}
              max={24}
              onChange={(v) => updateGlobal({ baseFontSize: v })}
            />

            <SliderControl
              label="Heading scale"
              icon={Maximize}
              value={g.headingScale}
              min={0.6}
              max={1.6}
              step={0.05}
              unit="×"
              onChange={(v) => updateGlobal({ headingScale: v })}
            />

            <ColorSwatchRow
              label="Body text colour"
              presets={grayPresets}
              value={g.bodyTextColor}
              onChange={(v) => updateGlobal({ bodyTextColor: v })}
            />

            <ColorSwatchRow
              label="Muted / helper text"
              presets={grayPresets}
              value={g.mutedTextColor}
              onChange={(v) => updateGlobal({ mutedTextColor: v })}
            />

            <ColorSwatchRow
              label="Primary accent"
              presets={accentPresets}
              value={g.accentColor}
              onChange={(v) => updateGlobal({ accentColor: v })}
            />
          </div>
        </section>

        {/* ── Per-section overrides ── */}
        <section>
          <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
            <Sliders className="w-5 h-5 text-[#A78BFA]" />
            Per-Section Overrides
          </h2>
          <p className="text-sm text-gray-400 mb-6">
            Expand a section to customise it independently. Sections without overrides inherit the global defaults.
          </p>

          <div className="space-y-2">
            {SECTION_META.map((s) => (
              <SectionPanel key={s.id} sectionId={s.id} sectionLabel={s.label} />
            ))}
          </div>
        </section>

        {/* ── Raw JSON view (for developers) ── */}
        <section>
          <details className="group">
            <summary className="cursor-pointer text-sm text-gray-400 hover:text-gray-300 transition-colors flex items-center gap-2">
              <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform" />
              Raw settings JSON (developer view)
            </summary>
            <pre className="mt-3 bg-[#070B14] border border-white/8 rounded-xl p-4 text-xs text-gray-300 overflow-x-auto max-h-80">
              {JSON.stringify(settings, null, 2)}
            </pre>
          </details>
        </section>

        {/* Spacer */}
        <div className="h-16" />
      </div>
    </div>
  );
}

/** Wrapper providing settings context. */
export default function PitchDeckAdminPage() {
  return (
    <PitchSettingsProvider>
      <AdminContent />
    </PitchSettingsProvider>
  );
}
